/**
 * RoTemplate - 轻量级 HTML 模板引擎（`<template>` + `data-bind-*`）。
 *
 * 支持：
 * - data-bind-text：文本
 * - data-bind-attr：`attr:path,attr2:path2`
 * - data-bind-show：显隐
 * - data-bind-if / elseif / else：条件节点物理移除
 * - data-bind-for：列表（写在子级 `<template>` 上）
 *
 * 不支持表达式运算、函数调用；路径仅支持点分层级（与原名一致）。
 *
 * --- data-bind-for ---
 * Legacy：整串为列表路径，如 stores、$item.categories；循环体内 $item / $this / $item.xxx、$index 指向当前最内层栈顶。
 * Vue 单行：store in stores、(category, ci) in store.categories；右侧仍为路径字符串。
 *
 * 注意：循环别名不要用 `$item`、`$index` 这类与内置前缀 `$item` / `$index` / `$item.xxx` 相同的名字，
 * 否则嵌套循环时 `_getValue` 会先将路径按「内置」解析到栈顶，可能取错对象；请改用 store、row 等标识符。
 *
 * 控制台：静态可识别的绑定错误使用 [RoTemplate] 前缀输出；同一 render 内按文案去重。
 *
 * 绑定隔离：完成对某结点的 text / attr / show / if 处理后写入 data-ro-*-done，查询时用 :not([...])，
 * 避免递归 _bind（如 list 外层与 item 内层）在错误 scopeStack 下二次处理后代。
 * 注意：if 根在「未挂 document、仍在 DocumentFragment 内」时 isConnected 为 false，仍会写入 data-ro-if-done，
 * 不能只依赖 isConnected 判断是否打标。
 */
class RoTemplate {
  /**
   * @typedef {{
   *   item: *,
   *   index: number,
   *   itemAlias: string | null,
   *   indexAlias: string | null
   * }} ScopeFrame
   */

  /**
   * @typedef {{
   *   diagnosticDedupKeys: Set<string>,
   *   templateLabel?: string | null,
   * }} DiagnosticContext
   */

  /** @type {RegExp} 匹配 ( item , index )，两侧各一个标识符 */
  static FOR_LHS_PAIR = /^\(\s*([A-Za-z_$][A-Za-z0-9_$]*)\s*,\s*([A-Za-z_$][A-Za-z0-9_$]*)\s*\)$/;

  /** @type {RegExp} 匹配单个 item 名 */
  static FOR_LHS_SINGLE = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

  static FOR_IN_SPLIT = /\s+in\s+/i;

  /** data-bind-text 已由本轮绑过，祖辈勿重复 */
  static RO_TEXT_DONE = 'data-ro-text-done';

  /** data-bind-attr 已由本轮处理 */
  static RO_ATTR_DONE = 'data-ro-attr-done';

  /** data-bind-show 已由本轮处理 */
  static RO_SHOW_DONE = 'data-ro-show-done';

  /** data-bind-if 根链已由本轮裁决 */
  static RO_IF_DONE = 'data-ro-if-done';

  /**
   * 渲染 <template id="...">
   * @param {string} templateId
   * @param {Object} data
   * @returns {DocumentFragment | null}
   */
  static render(templateId, data) {
    const templateEl =
      typeof document !== 'undefined' ? document.getElementById(templateId) : null;

    if (!templateEl || templateEl.tagName.toUpperCase() !== 'TEMPLATE') {
      RoTemplate._reportDiag(
        null,
        'error',
        `[RoTemplate] render 失败：不存在 id="${String(templateId)}" 的 <template>`,
      );

      return null;
    }

    const fragmentClone = templateEl.content.cloneNode(true);
    RoTemplate._bind(fragmentClone, data, [], {
      diagnosticDedupKeys: new Set(),
      templateLabel: templateId,
    });

    return fragmentClone;
  }

  /** @private @param {DiagnosticContext | null | undefined} ctx @param {'warn' | 'error'} level @param {string} message */
  static _reportDiag(ctx, level, message) {
    /** 附带模板 id，便于在页面多模板场景中定位来源 */
    const withCtx =
      ctx && ctx.templateLabel
        ? `${message} (模板 id=${RoTemplate._quoteAttr(String(ctx.templateLabel))})`
        : message;
    const key = `${level}::${withCtx}`;
    const bucket = ctx && ctx.diagnosticDedupKeys instanceof Set ? ctx.diagnosticDedupKeys : null;
    if (bucket && bucket.has(key)) {
      return;
    }
    if (bucket) {
      bucket.add(key);
    }

    if (level === 'error') {
      console.error(withCtx);
    } else {
      console.warn(withCtx);
    }
  }

  /**
   * 将单层 itemContext 兼容为单层栈帧；已为数组则原样返回。
   * @param {ScopeFrame[] | { item: *, index: number } | null | undefined} raw
   * @returns {ScopeFrame[]}
   */
  static _normalizeScopes(raw) {
    if (raw == null) {
      return [];
    }

    if (Array.isArray(raw)) {
      return raw;
    }

    if (typeof raw === 'object' && raw !== null && 'item' in raw && 'index' in raw) {
      /** @type {any} */

      /** @suppress {missingProperties} */
      const legacy = raw;

      return [
        {

          /** @suppress {missingProperties} */
          item: legacy.item,

          /** @suppress {missingProperties} */
          index: legacy.index,

          itemAlias:
            legacy.itemAlias != null && legacy.itemAlias !== undefined ? legacy.itemAlias : null,

          indexAlias:

            legacy.indexAlias != null && legacy.indexAlias !== undefined ? legacy.indexAlias : null,
        },
      ];
    }

    return [];
  }

  /** @private */
  static _quoteAttr(s) {
    return JSON.stringify(String(s ?? ''));
  }

  /**
   * 解析 data-bind-for：Vue 形 `item in path` / `(item,idx) in path`，否则整串为 legacy 列表路径。
   * grammarAbort 为 true 时调用方应移除此 `<template>` 且不展开列表。
   * @param {string | null} rawAttr
   * @param {DiagnosticContext | null | undefined} diag
   */
  static _parseDataBindFor(rawAttr, diag) {
    const trimmedRoot = typeof rawAttr === 'string' ? rawAttr.trim() : '';

    if (!trimmedRoot) {
      RoTemplate._reportDiag(diag, 'error', `[RoTemplate] data-bind-for 不能为空`);

      return {
        listSourcePath: '',
        itemAlias: null,
        indexAlias: null,
        grammarAbort: true,
      };
    }

    const parts = trimmedRoot.split(RoTemplate.FOR_IN_SPLIT);

    if (parts.length > 2) {
      RoTemplate._reportDiag(
        diag,

        'warn',

        `[RoTemplate] data-bind-for 含多处 " in "，已整条按 legacy 路径解析：${RoTemplate._quoteAttr(trimmedRoot)}`,
      );

      return {
        listSourcePath: trimmedRoot,
        itemAlias: null,
        indexAlias: null,
        grammarAbort: false,
      };
    }

    if (parts.length === 1) {
      return {
        listSourcePath: trimmedRoot,
        itemAlias: null,
        indexAlias: null,
        grammarAbort: false,
      };
    }

    const leftSide = parts[0].trim();
    const pathRight = parts[1].trim();

    if (!leftSide) {
      RoTemplate._reportDiag(
        diag,

        'error',

        `[RoTemplate] data-bind-for 中 " in " 左侧为空：${RoTemplate._quoteAttr(trimmedRoot)}`,
      );

      return {
        listSourcePath: pathRight,
        itemAlias: null,
        indexAlias: null,
        grammarAbort: true,
      };
    }

    if (!pathRight) {
      RoTemplate._reportDiag(
        diag,

        'error',

        `[RoTemplate] data-bind-for 右侧列表路径不能为空：${RoTemplate._quoteAttr(trimmedRoot)}`,
      );

      return {
        listSourcePath: '',
        itemAlias: null,
        indexAlias: null,
        grammarAbort: true,
      };
    }

    const pairMatched = RoTemplate.FOR_LHS_PAIR.exec(leftSide);

    if (pairMatched) {
      return {
        listSourcePath: pathRight,
        itemAlias: pairMatched[1],
        indexAlias: pairMatched[2],
        grammarAbort: false,
      };
    }

    if (RoTemplate.FOR_LHS_SINGLE.test(leftSide)) {
      return {
        listSourcePath: pathRight,
        itemAlias: leftSide,
        indexAlias: null,
        grammarAbort: false,
      };
    }

    RoTemplate._reportDiag(
      diag,

      'error',

      `[RoTemplate] data-bind-for 左侧格式错误（期望 item in path 或 (item,index) in path）：${RoTemplate._quoteAttr(trimmedRoot)}`,
    );

    return {
      listSourcePath: pathRight,
      itemAlias: null,
      indexAlias: null,
      grammarAbort: true,
    };
  }

  /**
   * 点分路径降级访问：段不存在时返回 null，不抛异常。
   * @param {*} root - 起始对象
   * @param {string[]} segments - 路径段
   */
  static _walkSegments(root, segments) {
    return segments.reduce((current, segment) => {
      if (current === null || current === undefined) return null;
      // 每段 trim：避免编辑器/格式化在「段名」两侧插入空白导致取不到字段（典型：phone 明明有却 data-bind-if 为假整条被删）
      const key = typeof segment === 'string' ? segment.trim() : String(segment ?? '').trim();
      if (!key) return null;
      return current[key];
    }, root);
  }

  /**
   * 读取绑定路径对应的值（根 data + 作用域别名 + `$item` / `$index`）。
   * @param {string|null|undefined} path
   * @param {Object|null|undefined} data
   * @param {ScopeFrame[]|null|undefined|Object} scopesIn - 帧数组或 legacy 单层对象
   */
  static _getValue(path, data, scopesIn) {
    if (path === null || path === undefined || String(path).trim() === '') {
      return null;
    }

    const pathStr = String(path).trim();
    const stack = RoTemplate._normalizeScopes(scopesIn);
    const top = stack.length ? stack[stack.length - 1] : null;

    if (pathStr === '$index') {
      return top ? top.index : null;
    }

    if (pathStr === '$item' || pathStr === '$this') {
      return top ? top.item : data;
    }

    if (pathStr.startsWith('$item.') || pathStr.startsWith('$this.')) {
      if (!top) return null;
      const rest = pathStr
        .split('.')
        .slice(1)
        .map((seg) => seg.trim())
        .filter((seg) => seg.length > 0);
      return RoTemplate._walkSegments(top.item, rest);
    }

    // 自内向外：索引别名整条匹配，再匹配 item 别名首段
    for (let i = stack.length - 1; i >= 0; i--) {
      const frame = stack[i];
      if (frame.indexAlias && pathStr === frame.indexAlias) {
        return frame.index;
      }
    }

    for (let j = stack.length - 1; j >= 0; j--) {
      const frame = stack[j];
      if (!frame.itemAlias) continue;
      const parts = pathStr
        .split('.')
        .map((segment) => segment.trim())
        .filter((segment) => segment.length > 0);
      if (!parts.length) continue;
      if (parts[0] === frame.itemAlias) {
        return RoTemplate._walkSegments(frame.item, parts.slice(1));
      }
    }

    if (typeof data !== 'object' || data === null) {
      return null;
    }

    const dataParts = pathStr
      .split('.')
      .map((segment) => segment.trim())
      .filter((segment) => segment.length > 0);
    return RoTemplate._walkSegments(data, dataParts);
  }

  /**
   * 空字符串绑定的提醒（避免模板里写了属性却以为已绑定）。
   * @param {DiagnosticContext|null|undefined} diag
   * @param {string} kind
   * @param {string|null} raw
   */
  static _warnEmptyBind(diag, kind, raw) {
    RoTemplate._reportDiag(
      diag,
      'warn',
      `[RoTemplate] ${kind} 的值为空字符串，已忽略该绑定。原始=${RoTemplate._quoteAttr(raw)}`,
    );
  }

  /**
   * 递归绑定 fragment / 子树。
   * @param {Node} container
   * @param {Object} data
   * @param {ScopeFrame[]|Object|null|undefined} scopesIn
   * @param {DiagnosticContext|null|undefined} diag
   */
  static _bind(container, data, scopesIn = null, diag = null) {
    const scopeStack = RoTemplate._normalizeScopes(scopesIn);

    const forTemplates = Array.from(container.querySelectorAll('template[data-bind-for]'));
    const textElements = Array.from(
      container.querySelectorAll(`[data-bind-text]:not([${RoTemplate.RO_TEXT_DONE}])`),
    );
    const attrElements = Array.from(
      container.querySelectorAll(`[data-bind-attr]:not([${RoTemplate.RO_ATTR_DONE}])`),
    );
    const showElements = Array.from(
      container.querySelectorAll(`[data-bind-show]:not([${RoTemplate.RO_SHOW_DONE}])`),
    );

    forTemplates.forEach((templateEl) => {
      const rawDirective = templateEl.getAttribute('data-bind-for');
      const parsedDirective = RoTemplate._parseDataBindFor(rawDirective, diag);

      if (parsedDirective.grammarAbort) {
        templateEl.remove();
        return;
      }

      const listValue = RoTemplate._getValue(parsedDirective.listSourcePath, data, scopeStack);
      const parentNode = templateEl.parentNode;

      if (!parentNode) {
        return;
      }

      // 与历史行为一致：非数组则不展开列表，并保留 <template> 占位
      if (!Array.isArray(listValue)) {
        return;
      }

      listValue.forEach((rowItem, rowIndex) => {
        const rowFragment = templateEl.content.cloneNode(true);
        const nextStack = [
          ...scopeStack,
          {
            item: rowItem,
            index: rowIndex,
            itemAlias: parsedDirective.itemAlias,
            indexAlias: parsedDirective.indexAlias,
          },
        ];
        RoTemplate._bind(rowFragment, data, nextStack, diag);
        parentNode.insertBefore(rowFragment, templateEl);
      });
    });

    textElements.forEach((el) => {
      const key = String(el.getAttribute('data-bind-text') ?? '').trim();
      if (!key) {
        RoTemplate._warnEmptyBind(diag, 'data-bind-text', el.getAttribute('data-bind-text'));
        el.setAttribute(RoTemplate.RO_TEXT_DONE, '');
        return;
      }

      const value = RoTemplate._getValue(key, data, scopeStack);
      if (value !== null && value !== undefined) {
        el.textContent = typeof value === 'object' ? JSON.stringify(value) : String(value);
      }

      el.setAttribute(RoTemplate.RO_TEXT_DONE, '');
    });

    attrElements.forEach((el) => {
      const bindingStr = el.getAttribute('data-bind-attr');
      if (!bindingStr) {
        RoTemplate._warnEmptyBind(diag, 'data-bind-attr', bindingStr);
        el.setAttribute(RoTemplate.RO_ATTR_DONE, '');
        return;
      }

      bindingStr.split(',').forEach((chunk, indexZero) => {
        const piece = chunk.trim();
        if (!piece) {
          return;
        }

        const colonIndex = piece.indexOf(':');
        if (colonIndex < 0) {
          RoTemplate._reportDiag(
            diag,
            'warn',
            `[RoTemplate] data-bind-attr 第 ${indexZero + 1} 段缺少冒号（应为 attr:path）。内容=${RoTemplate._quoteAttr(piece)}`,
          );
          return;
        }

        const attrName = piece.slice(0, colonIndex).trim();
        const pathKey = piece.slice(colonIndex + 1).trim();

        if (!attrName || !pathKey) {
          RoTemplate._reportDiag(
            diag,
            'warn',
            `[RoTemplate] data-bind-attr 第 ${indexZero + 1} 段 attr 或 path 为空。内容=${RoTemplate._quoteAttr(piece)}`,
          );
          return;
        }

        const resolved = RoTemplate._getValue(pathKey, data, scopeStack);
        if (resolved !== null && resolved !== undefined) {
          el.setAttribute(
            attrName,
            typeof resolved === 'object' ? JSON.stringify(resolved) : String(resolved),
          );
        }
      });

      el.setAttribute(RoTemplate.RO_ATTR_DONE, '');
    });

    showElements.forEach((el) => {
      const key = String(el.getAttribute('data-bind-show') ?? '').trim();
      if (!key) {
        RoTemplate._warnEmptyBind(diag, 'data-bind-show', el.getAttribute('data-bind-show'));
        el.setAttribute(RoTemplate.RO_SHOW_DONE, '');
        return;
      }

      const value = RoTemplate._getValue(key, data, scopeStack);
      el.style.display = value ? '' : 'none';

      el.setAttribute(RoTemplate.RO_SHOW_DONE, '');
    });

    const ifRoots = Array.from(
      container.querySelectorAll(`[data-bind-if]:not([${RoTemplate.RO_IF_DONE}])`),
    );

    ifRoots.forEach((el) => {
      const ifKeyRaw = el.getAttribute('data-bind-if');
      const ifKey = String(ifKeyRaw ?? '').trim();
      if (!ifKey) {
        RoTemplate._warnEmptyBind(diag, 'data-bind-if', ifKeyRaw);
      }

      let satisfied = !!RoTemplate._getValue(ifKey, data, scopeStack);
      let next = el.nextElementSibling;

      if (!satisfied) {
        el.remove();
      }

      while (next && (next.hasAttribute('data-bind-elseif') || next.hasAttribute('data-bind-else'))) {
        const current = next;
        next = next.nextElementSibling;

        if (satisfied) {
          current.remove();
          continue;
        }

        if (current.hasAttribute('data-bind-elseif')) {
          const elseifKeyRaw = current.getAttribute('data-bind-elseif');
          const elseifKey = String(elseifKeyRaw ?? '').trim();
          if (!elseifKey) {
            RoTemplate._warnEmptyBind(diag, 'data-bind-elseif', elseifKeyRaw);
          }

          const elseifVal = !!RoTemplate._getValue(elseifKey, data, scopeStack);
          if (elseifVal) {
            satisfied = true;
          } else {
            current.remove();
          }
        } else if (current.hasAttribute('data-bind-else')) {
          satisfied = true;
        }
      }

      // 未挂到 document 的节点（例如 render 仍在 DocumentFragment 内）isConnected 为 false，
      // 但必须打标 RO_IF_DONE；否则外层 _bind 会以空 scopeStack 再次扫描 :not([data-ro-if-done])，
      // 把 store.xxx 误解析成 data.store 并错误 remove（典型：卡片 Tel 整段消失）。
      if (satisfied) {
        el.setAttribute(RoTemplate.RO_IF_DONE, '');
      }
    });
  }
}

typeof window !== 'undefined' && (window.RoTemplate = RoTemplate);
